import { useContext, type FC } from 'react';

import ThemeContext from 'app/common/ThemeContext';

import style from './style.module.scss';

interface Props extends ClassNameProp {
  dire: GameOpponentDota2 | LiveGameOpponentDota2 | AggregatedTeamDota2 | undefined;
  radiant: GameOpponentDota2 | LiveGameOpponentDota2 | AggregatedTeamDota2 | undefined;
  winner: SideDota2 | undefined | null;
  withBarracks?: boolean;
  // Optional per-instance side colors (grad widget uses board-position colors);
  // default to the theme's radiant/dire tokens.
  radiantColor?: string;
  direColor?: string;
}

const isStillUp = (value: boolean | undefined | null): string =>
  value !== false ? '' : style.isDestroyed;

const Dota2Map: FC<Props> = ({
  dire,
  radiant,
  winner,
  className,
  withBarracks = false,
  radiantColor,
  direColor
}) => {
  const { theme } = useContext(ThemeContext);
  const { background_secondary } = theme;
  const vg_dota_radiant = radiantColor ?? theme.vg_dota_radiant;
  const vg_dota_dire = direColor ?? theme.vg_dota_dire;

  const withDireBarracks = withBarracks && dire && 'barracks_status' in dire;
  const withRadiantBarracks = withBarracks && radiant && 'barracks_status' in radiant;

  return (
    <svg
      width="200"
      height="200"
      viewBox="0 0 200 200"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M62.3053 0H106.542V15.5763H62.3053C36.4976 15.5763 15.5763 36.4976 15.5763 62.3053V93.7695H0V62.3053C0 27.895 27.895 0 62.3053 0ZM0 93.7695V184.424V200H15.5763H106.542V184.424H15.5763V93.7695H0ZM184.424 93.7695V137.695C184.424 163.502 163.502 184.424 137.695 184.424H106.542V200H137.695C172.105 200 200 172.105 200 137.695V93.7695H184.424ZM200 93.7695V15.5763V0H184.424H106.542V15.5763H184.424V93.7695H200Z"
        fill={background_secondary}
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M139.184 70.7673L193.583 16.3684L182.562 5.3476L128.163 59.7465L139.184 70.7673ZM128.163 59.7465L68.9373 118.972L79.9581 129.993L139.184 70.7673L128.163 59.7465ZM68.9373 118.972L79.9581 129.993L16.3684 193.583L5.3476 182.562L68.9373 118.972Z"
        fill={background_secondary}
      />
      {withRadiantBarracks && (
        <>
          {/* bottom melee */}
          <g className={isStillUp(radiant?.barracks_status?.bottom_melee)}>
            <rect x="25.2407" y="193.497" width="4.81283" height="3.08021" fill={vg_dota_radiant} />
            <rect x="26.0429" y="192.727" width="3.20856" height="1.54011" fill={vg_dota_radiant} />
          </g>
          {/* bottom ranged */}
          <g className={isStillUp(radiant?.barracks_status?.bottom_ranged)}>
            <rect x="25.2407" y="187.721" width="4.81283" height="3.08021" fill={vg_dota_radiant} />
            <rect x="26.0429" y="186.952" width="3.20856" height="1.54011" fill={vg_dota_radiant} />
          </g>
          {/* top melee */}
          <g className={isStillUp(radiant?.barracks_status?.top_melee)}>
            <rect x="7.91452" y="170.395" width="4.81283" height="3.08021" fill={vg_dota_radiant} />
            <rect x="8.71666" y="169.626" width="3.20856" height="1.54011" fill={vg_dota_radiant} />
          </g>
          {/* top ranged */}
          <g className={isStillUp(radiant?.barracks_status?.top_ranged)}>
            <rect x="2.13912" y="170.395" width="4.81283" height="3.08021" fill={vg_dota_radiant} />
            <rect x="2.94126" y="169.626" width="3.20856" height="1.54011" fill={vg_dota_radiant} />
          </g>
          {/* middle melee */}
          <g className={isStillUp(radiant?.barracks_status?.middle_melee)}>
            <rect x="19.4653" y="180.021" width="4.81283" height="3.08021" fill={vg_dota_radiant} />
            <rect x="20.2675" y="179.251" width="3.20856" height="1.54011" fill={vg_dota_radiant} />
          </g>
          {/* middle ranged */}
          <g className={isStillUp(radiant?.barracks_status?.middle_ranged)}>
            <rect x="15.6151" y="174.245" width="4.81283" height="3.08021" fill={vg_dota_radiant} />
            <rect x="16.4172" y="173.476" width="3.20856" height="1.54011" fill={vg_dota_radiant} />
          </g>
        </>
      )}
      {/* middle 1 */}
      <path
        className={isStillUp(radiant?.tower_status?.middle_tier_1)}
        d="M84.5344 109.946V111.134H83.1863V109.946H81.8406V111.134H80.4925V109.946H79.1445V112.415L80.1858 113.009V118.114H79.1445V120.155H82.5192H85.8824V118.114H84.8717V113.009L85.8824 112.415V109.946H84.5344Z"
        fill={vg_dota_radiant}
      />
      {/* middle 2 */}
      <path
        className={isStillUp(radiant?.tower_status?.middle_tier_2)}
        d="M55.6574 138.823V140.011H54.3093V138.823H52.9636V140.011H51.6155V138.823H50.2675V141.292L51.3088 141.886V146.991H50.2675V149.032H53.6422H57.0054V146.991H55.9947V141.886L57.0054 141.292V138.823H55.6574Z"
        fill={vg_dota_radiant}
      />
      {/* middle 3 */}
      <path
        className={isStillUp(radiant?.tower_status?.middle_tier_3)}
        d="M28.7055 165.775V166.963H27.3575V165.775H26.0117V166.963H24.6636V165.775H23.3156V168.244L24.3569 168.838V173.943H23.3156V175.984H26.6903H30.0536V173.943H29.0429V168.838L30.0536 168.244V165.775H28.7055Z"
        fill={vg_dota_radiant}
      />
      {/* top 1 */}
      <path
        className={isStillUp(radiant?.tower_status?.top_tier_1)}
        d="M9.10382 59.893V61.0033H7.84339V59.893H6.58511V61.0033H5.32468V59.893H4.06425V62.2014L5.03789 62.7566V67.5293H4.06425V69.4384H7.21963H10.3643V67.5293H9.41925V62.7566L10.3643 62.2014V59.893H9.10382Z"
        fill={vg_dota_radiant}
      />
      {/* top 2 */}
      <path
        className={isStillUp(radiant?.tower_status?.top_tier_2)}
        d="M9.10382 104.893V106.003H7.84339V104.893H6.58511V106.003H5.32468V104.893H4.06425V107.201L5.03789 107.757V112.529H4.06425V114.438H7.21963H10.3643V112.529H9.41925V107.757L10.3643 107.201V104.893H9.10382Z"
        fill={vg_dota_radiant}
      />
      {/* top 3 */}
      <path
        className={isStillUp(radiant?.tower_status?.top_tier_3)}
        d="M9.10382 149.893V151.003H7.8434V149.893H6.58511V151.003H5.32468V149.893H4.06425V152.201L5.03789 152.757V157.529H4.06425V159.438H7.21963H10.3643V157.529H9.41926V152.757L10.3643 152.201V149.893H9.10382Z"
        fill={vg_dota_radiant}
      />
      {/* bottom 1 */}
      <path
        className={isStillUp(radiant?.tower_status?.bottom_tier_1)}
        d="M124.146 187.556V188.667H122.886V187.556H121.627V188.667H120.367V187.556H119.106V189.865L120.08 190.42V195.193H119.106V197.102H122.262H125.406V195.193H124.461V190.42L125.406 189.865V187.556H124.146Z"
        fill={vg_dota_radiant}
      />
      {/* bottom 2 */}
      <path
        className={isStillUp(radiant?.tower_status?.bottom_tier_2)}
        d="M82.206 187.556V188.667H80.9455V187.556H79.6872V188.667H78.4268V187.556H77.1664V189.865L78.14 190.42V195.193H77.1664V197.102H80.3218H83.4664V195.193H82.5214V190.42L83.4664 189.865V187.556H82.206Z"
        fill={vg_dota_radiant}
      />
      {/* bottom 3 */}
      <path
        className={isStillUp(radiant?.tower_status?.bottom_tier_3)}
        d="M39.906 187.284V188.394H38.6455V187.284H37.3873V188.394H36.1268V187.284H34.8664V189.592L35.84 190.147V194.92H34.8664V196.829H38.0218H41.1664V194.92H40.2214V190.147L41.1664 189.592V187.284H39.906Z"
        fill={vg_dota_radiant}
      />
      {/* ancient top */}
      <path
        className={isStillUp(radiant?.tower_status?.ancient_top)}
        d="M9.45417 177.326V178.514H8.10612V177.326H6.76036V178.514H5.4123V177.326H4.06425V179.795L5.10558 180.389V185.493H4.06425V187.535H7.43898H10.8022V185.493H9.79153V180.389L10.8022 179.795V177.326H9.45417Z"
        fill={vg_dota_radiant}
      />
      {/* ancient bottom */}
      <path
        className={isStillUp(radiant?.tower_status?.ancient_bottom)}
        d="M19.0798 186.952V188.139H17.7318V186.952H16.386V188.139H15.038V186.952H13.6899V189.421L14.7312 190.015V195.119H13.6899V197.161H17.0647H20.4279V195.119H19.4172V190.015L20.4279 189.421V186.952H19.0798Z"
        fill={vg_dota_radiant}
      />
      <circle
        className={isStillUp(winner !== 'dire')}
        cx="6.95196"
        cy="193.69"
        r="2.8877"
        fill={vg_dota_radiant}
      />
      <circle
        className={isStillUp(winner !== 'radiant')}
        cx="191.765"
        cy="6.95192"
        r="2.8877"
        fill={vg_dota_dire}
      />
      {/* ancient bottom */}
      <path
        className={isStillUp(dire?.tower_status?.ancient_bottom)}
        d="M194.267 12.3246V13.4443H192.919V12.3246H191.573V13.4443H190.225V12.3246H188.877V14.6525L189.918 15.2123V20.0252H188.877V21.9503H192.252H195.615V20.0252H194.604V15.2123L195.615 14.6525V12.3246H194.267Z"
        fill={vg_dota_dire}
      />
      {/* ancient top */}
      <path
        className={isStillUp(dire?.tower_status?.ancient_top)}
        d="M184.641 2.69896V3.8186H183.293V2.69896H181.947V3.8186H180.599V2.69896H179.251V5.02683L180.293 5.58666V10.3995H179.251V12.3246H182.626H185.989V10.3995H184.979V5.58666L185.989 5.02683V2.69896H184.641Z"
        fill={vg_dota_dire}
      />
      {/* middle 1 */}
      <path
        className={isStillUp(dire?.tower_status?.middle_tier_1)}
        d="M117.262 77.7792V78.8988H115.913V77.7792H114.568V78.8988H113.22V77.7792H111.872V80.107L112.913 80.6669V85.4797H111.872V87.4048H115.246H118.61V85.4797H117.599V80.6669L118.61 80.107V77.7792H117.262Z"
        fill={vg_dota_dire}
      />
      {/* middle 2 */}
      <path
        className={isStillUp(dire?.tower_status?.middle_tier_2)}
        d="M144.213 50.8273V51.9469H142.865V50.8273H141.52V51.9469H140.172V50.8273H138.824V53.1552L139.865 53.715V58.5278H138.824V60.453H142.198H145.561V58.5278H144.551V53.715L145.561 53.1552V50.8273H144.213Z"
        fill={vg_dota_dire}
      />
      {/* middle 3 */}
      <path
        className={isStillUp(dire?.tower_status?.middle_tier_3)}
        d="M173.09 21.9503V23.0699H171.742V21.9503H170.397V23.0699H169.049V21.9503H167.701V24.2782L168.742 24.838V29.6508H167.701V31.576H171.075H174.438V29.6508H173.428V24.838L174.438 24.2782V21.9503H173.09Z"
        fill={vg_dota_dire}
      />
      {/* bottom 1 */}
      <path
        className={isStillUp(dire?.tower_status?.bottom_tier_1)}
        d="M193.917 125.39V126.437H192.656V125.39H191.398V126.437H190.137V125.39H188.877V127.566L189.851 128.09V132.59H188.877V134.39H192.032H195.177V132.59H194.232V128.09L195.177 127.566V125.39H193.917Z"
        fill={vg_dota_dire}
      />
      {/* bottom 2 */}
      <path
        className={isStillUp(dire?.tower_status?.bottom_tier_2)}
        d="M193.917 80.3898V81.4367H192.656V80.3898H191.398V81.4367H190.137V80.3898H188.877V82.5664L189.851 83.0898V87.5898H188.877V89.3898H192.032H195.177V87.5898H194.232V83.0898L195.177 82.5664V80.3898H193.917Z"
        fill={vg_dota_dire}
      />
      {/* bottom 3 */}
      <path
        className={isStillUp(dire?.tower_status?.bottom_tier_3)}
        d="M193.917 35.3898V36.4367H192.656V35.3898H191.398V36.4367H190.137V35.3898H188.877V37.5664L189.851 38.0898V42.5898H188.877V44.3898H192.032H195.177V42.5898H194.232V38.0898L195.177 37.5664V35.3898H193.917Z"
        fill={vg_dota_dire}
      />
      {/* top 1 */}
      <path
        className={isStillUp(dire?.tower_status?.top_tier_1)}
        d="M74.5583 2.97535V4.02222H73.2979V2.97535H72.0396V4.02222H70.7791V2.97535H69.5187V5.15191L70.4924 5.67535V10.1754H69.5187V11.9754H72.6741H75.8187V10.1754H74.8737V5.67535L75.8187 5.15191V2.97535H74.5583Z"
        fill={vg_dota_dire}
      />
      {/* top 2 */}
      <path
        className={isStillUp(dire?.tower_status?.top_tier_2)}
        d="M116.858 2.97535V4.02222H115.598V2.97535H114.34V4.02222H113.079V2.97535H111.819V5.15191L112.792 5.67535V10.1754H111.819V11.9754H114.974H118.119V10.1754H117.174V5.67535L118.119 5.15191V2.97535H116.858Z"
        fill={vg_dota_dire}
      />
      {/* top 3 */}
      <path
        className={isStillUp(dire?.tower_status?.top_tier_3)}
        d="M159.158 2.97535V4.02222H157.898V2.97535H156.64V4.02222H155.379V2.97535H154.119V5.15191L155.092 5.67535V10.1754H154.119V11.9754H157.274H160.419V10.1754H159.474V5.67535L160.419 5.15191V2.97535H159.158Z"
        fill={vg_dota_dire}
      />
      {withDireBarracks && (
        <>
          {/* bottom ranged */}
          <g className={isStillUp(dire?.barracks_status?.bottom_ranged)}>
            <rect x="186.952" y="27.9354" width="4.81283" height="3.08021" fill={vg_dota_dire} />
            <rect x="187.754" y="27.1658" width="3.20856" height="1.54011" fill={vg_dota_dire} />
          </g>
          {/* bottom melee */}
          <g className={isStillUp(dire?.barracks_status?.bottom_melee)}>
            <rect x="192.727" y="27.9354" width="4.81283" height="3.08021" fill={vg_dota_dire} />
            <rect x="193.529" y="27.1658" width="3.20856" height="1.54011" fill={vg_dota_dire} />
          </g>
          {/* middle ranged */}
          <g className={isStillUp(dire?.barracks_status?.middle_ranged)}>
            <rect x="173.476" y="16.3846" width="4.81283" height="3.08021" fill={vg_dota_dire} />
            <rect x="174.278" y="15.615" width="3.20856" height="1.54011" fill={vg_dota_dire} />
          </g>
          {/* middle melee */}
          <g className={isStillUp(dire?.barracks_status?.middle_melee)}>
            <rect x="177.326" y="22.16" width="4.81283" height="3.08021" fill={vg_dota_dire} />
            <rect x="178.128" y="21.3904" width="3.20856" height="1.54011" fill={vg_dota_dire} />
          </g>
          {/* top ranged */}
          <g className={isStillUp(dire?.barracks_status?.top_ranged)}>
            <rect x="169.626" y="2.90864" width="4.81283" height="3.08021" fill={vg_dota_dire} />
            <rect x="170.428" y="2.13908" width="3.20856" height="1.54011" fill={vg_dota_dire} />
          </g>
          {/* top melee */}
          <g className={isStillUp(dire?.barracks_status?.top_melee)}>
            <rect x="169.626" y="8.68404" width="4.81283" height="3.08021" fill={vg_dota_dire} />
            <rect x="170.428" y="7.91449" width="3.20856" height="1.54011" fill={vg_dota_dire} />
          </g>
        </>
      )}
    </svg>
  );
};

Dota2Map.displayName = 'Dota2Map';

export default Dota2Map;
